/* eslint-disable react/no-array-index-key -- These will never be reordered so it doesn't matter */
import { ActionableTrainSectionWrapper } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/ActionableTrains/actionableTrainSectionHelpers";
import { DashedLinesGap } from "features/CenterContent/RoleContent/ProductionDeviation/components/DashedLinesGap";
import {
  Box,
  HStack,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Stack,
  VStack,
} from "shared/components/Spor";
import {
  SkeletonButton,
  SkeletonElement,
  SkeletonElementWrapper,
  SkeletonRow,
  SkeletonWrapper,
} from "shared/components/feedback/SkeletonLoader/SkeletonLoaderStyles";
import { range } from "shared/utils/arrayUtils";

type SkeletonType =
  | "list"
  | "listBig"
  | "trainmap"
  | "trainStops"
  | "trainList"
  | "trainInfoForm"
  | "selectTrains"
  | "affectedTrains"
  | "internalEvents"
  | "reasonCode"
  | "tilsynAlertsFullSize"
  | "tilsynAlerts"
  | "trainReasonCodes"
  | "materialContent"
  | "oplogg"
  | "todo"
  | "trainComposition"
  | "selectReserveDropdown"
  | "trainSeries"
  | "actionableTrains";

const ListSkeleton = ({ height }: { height?: string }) => (
  <SkeletonWrapper>
    {Array.from(Array(3)).map((_, index) => (
      <SkeletonElement key={index} style={{ height }} />
    ))}
  </SkeletonWrapper>
);

const TrainMapSkeleton = () => (
  <SkeletonWrapper style={{ borderRadius: "6px" }}>
    <SkeletonElement
      style={{
        margin: "0px",
        height: "35rem",
        borderRadius: "6px 6px 0 0",
      }}
    />
  </SkeletonWrapper>
);

const TrainStopsSkeleton = () => (
  <SkeletonWrapper>
    <SkeletonElement style={{ margin: "3rem auto", width: "80%" }} />
  </SkeletonWrapper>
);

const TrainListSkeleton = () => (
  <>
    {Array.from(Array(3)).map((_r, rowIndex) => (
      <SkeletonWrapper key={rowIndex} style={{ height: "auto" }} $borderBottom>
        <SkeletonButton style={{ marginRight: "12px" }} />
        <SkeletonButton />
        {Array.from(Array(3)).map((_c, colIndex) => (
          <SkeletonElement
            key={colIndex}
            style={{ width: "60%", margin: "15px" }}
          />
        ))}
      </SkeletonWrapper>
    ))}
  </>
);

const TrainInfoFormSkeleton = () => (
  <>
    <Stack gap="12px">
      <Skeleton height="36px" />
      <Skeleton height="36px" />
    </Stack>
    <SkeletonText lineClamp="3" width="30%" height="54px" />
  </>
);

const SelectTrainsSkeleton = () => (
  <DashedLinesGap variant="secondary">
    {[1, 2, 10].map((size) => (
      <SkeletonWrapper key={size} style={{ height: "auto" }} $borderBottom>
        <Skeleton height={`${size * 40}px`} borderBottomRadius={0} />
      </SkeletonWrapper>
    ))}
  </DashedLinesGap>
);

const AffectedTrainsSkeleton = () => (
  <>
    <SkeletonWrapper style={{ height: "auto", backgroundColor: "transparent" }}>
      <VStack width="100%" p="12px" gap="16px">
        {Array.from(Array(3)).map((_, i) => (
          <Skeleton key={i} height="42px" width="100%" borderRadius="10" />
        ))}
      </VStack>
    </SkeletonWrapper>
    <SkeletonWrapper style={{ height: "auto", backgroundColor: "transparent" }}>
      <SkeletonText width="60%" margin="16px" />

      <HStack width="100%" p="12px" gap="40px">
        {Array.from(Array(2)).map((_, i) => (
          <Skeleton key={i} height="42px" width="160px" borderRadius="10" />
        ))}
      </HStack>
      <HStack width="100%" p="12px" gap="40px">
        <Skeleton height="42px" width="200px" borderRadius="10" />
      </HStack>
    </SkeletonWrapper>
  </>
);

const TrainReasonCodesSkeleton = () => (
  <SkeletonWrapper>
    <SkeletonRow style={{ justifyContent: "space-between" }}>
      {Array.from(Array(5)).map((_, index) => (
        <SkeletonElement key={index} style={{ width: "12%" }} />
      ))}
    </SkeletonRow>
  </SkeletonWrapper>
);

const TilsynAlertsFullSizeSkeleton = () => (
  <SkeletonWrapper>
    {Array.from(Array(3)).map((_r, rowIndex) => (
      <SkeletonRow key={rowIndex}>
        {Array.from(Array(6)).map((_c, colIndex) => (
          <SkeletonElement
            key={colIndex}
            style={{ width: "8%", margin: "15px 7% 15px 0px" }}
          />
        ))}
        <SkeletonButton
          style={{ margin: "0px", height: "36px", width: "65px" }}
        />
      </SkeletonRow>
    ))}
  </SkeletonWrapper>
);

const TilsynAlertsSkeleton = () => (
  <SkeletonWrapper>
    <SkeletonRow style={{ justifyContent: "space-between" }}>
      {Array.from(Array(4)).map((_, index) => (
        <SkeletonElement key={index} style={{ width: "12%" }} />
      ))}
    </SkeletonRow>
  </SkeletonWrapper>
);

const MaterialContentSkeleton = () => (
  <SkeletonWrapper>
    {Array.from(Array(5)).map((_, index) => (
      <SkeletonElement
        key={index}
        style={{
          width: "32%",
          height: "35px",
          margin: "10px 10px 20px 10px",
        }}
      />
    ))}
  </SkeletonWrapper>
);

const ReasonCodeSkeleton = () => (
  <SkeletonWrapper>
    {Array.from(Array(3)).map((_r, rowIndex) => (
      <SkeletonRow key={rowIndex} style={{ margin: "0px" }}>
        {Array.from(Array(6)).map((_c, colIndex) => (
          <SkeletonElement
            key={colIndex}
            style={{ width: "10%", margin: "15px 2% 0px 0px" }}
          />
        ))}
      </SkeletonRow>
    ))}
  </SkeletonWrapper>
);

const OploggSkeleton = () => (
  <SkeletonWrapper>
    {Array.from(Array(3)).map((_, index) => (
      <SkeletonElementWrapper key={index}>
        <SkeletonElement
          style={{ width: "100%", margin: "0px 0px 45px 0px" }}
        />
        <SkeletonButton
          style={{ width: "95px", height: "12px", margin: "-32px 0px" }}
        />
      </SkeletonElementWrapper>
    ))}
  </SkeletonWrapper>
);

const TodoSkeleton = () => (
  <>
    {Array.from(Array(2)).map((_, index) => (
      <SkeletonWrapper key={index} style={{ height: "auto" }} $borderBottom>
        <SkeletonElementWrapper>
          <SkeletonButton
            style={{ width: "69px", height: "36px", margin: "0px -8px" }}
          />
          <SkeletonElement style={{ width: "95px", margin: "0px" }} />
          <SkeletonElement style={{ width: "304px", margin: "8px 0px" }} />
          <SkeletonElement
            style={{
              width: "95px",
              height: "12px",
              margin: "25px 0px 0px 0px",
            }}
          />
        </SkeletonElementWrapper>
      </SkeletonWrapper>
    ))}
  </>
);

const TrainCompositionSkeleton = () => (
  <SkeletonWrapper>
    <SkeletonElement
      style={{ height: "30px", width: "60%", margin: "50px auto" }}
    />
  </SkeletonWrapper>
);

const SelectReserveDropdownSkeleton = () => (
  <SkeletonWrapper>
    <SkeletonElement style={{ width: "100%", height: "42px" }} />
  </SkeletonWrapper>
);

const InternalEventsSkeleton = () => (
  <VStack gap="6px" w="100%">
    {range(3).map((i) => (
      <HStack
        key={i}
        w="100%"
        justify="space-between"
        alignItems="flex-top"
        h="100px"
      >
        <SkeletonText lineClamp="4" w="70%" />
        <SkeletonCircle />
      </HStack>
    ))}
  </VStack>
);

const TrainSeriesSkeleton = () => (
  <VStack gap="0px" w="100%">
    <SkeletonElement />
  </VStack>
);

const ActionableTrainsSkeleton = () => (
  <HStack>
    {range(3).map((i) => (
      <ActionableTrainSectionWrapper key={i} $collapsed={false}>
        <SkeletonWrapper>
          <SkeletonElement
            style={{ width: "60%", maxWidth: "400px", height: "1.5em" }}
          />
          <SkeletonElement style={{ height: "100px" }} />
          <SkeletonElement style={{ height: "200px", marginTop: "40px" }} />
          <SkeletonElement style={{ height: "300px", marginTop: "40px" }} />
        </SkeletonWrapper>
      </ActionableTrainSectionWrapper>
    ))}
  </HStack>
);

const SkeletonLoaderContent = ({
  skeletonType,
}: {
  skeletonType: SkeletonType;
}) => {
  switch (skeletonType) {
    case "list":
      return <ListSkeleton />;
    case "listBig":
      return <ListSkeleton height="60px" />;
    case "trainmap":
      return <TrainMapSkeleton />;
    case "trainStops":
      return <TrainStopsSkeleton />;
    case "trainList":
      return <TrainListSkeleton />;
    case "trainInfoForm":
      return <TrainInfoFormSkeleton />;
    case "selectTrains":
      return <SelectTrainsSkeleton />;
    case "affectedTrains":
      return <AffectedTrainsSkeleton />;
    case "reasonCode":
      return <ReasonCodeSkeleton />;
    case "trainReasonCodes":
      return <TrainReasonCodesSkeleton />;
    case "tilsynAlertsFullSize":
      return <TilsynAlertsFullSizeSkeleton />;
    case "tilsynAlerts":
      return <TilsynAlertsSkeleton />;
    case "materialContent":
      return <MaterialContentSkeleton />;
    case "oplogg":
      return <OploggSkeleton />;
    case "todo":
      return <TodoSkeleton />;
    case "trainComposition":
      return <TrainCompositionSkeleton />;
    case "selectReserveDropdown":
      return <SelectReserveDropdownSkeleton />;
    case "internalEvents":
      return <InternalEventsSkeleton />;
    case "trainSeries":
      return <TrainSeriesSkeleton />;
    case "actionableTrains":
      return <ActionableTrainsSkeleton />;
  }
};

// Adds an invincible box recongnizeable for tests
const SkeletonLoader = ({ skeletonType }: { skeletonType: SkeletonType }) => (
  <>
    <SkeletonLoaderContent skeletonType={skeletonType} />
    <Box data-testid="skeleton" position="absolute" maxH={0} p="0 !important" />
  </>
);

export default SkeletonLoader;
